import { useEffect, useMemo, useState } from 'react';
import {
  Anchor, Badge, Button, Group, Modal, ScrollArea, Select, Stack, Table, Text,
} from '@mantine/core';
import { IconDownload, IconExternalLink, IconSearch } from '@tabler/icons-react';
import { TextInput } from '@/components/ui/inputs';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import { downloadCsv } from '../ExportCsv';
import { recordPath } from '../Enrich/recordLinks';
import {
  INCOMPLETE_FILTER_OPTIONS, type IncompleteKey, type IncompleteRow,
} from '../Enrich/incomplete';

interface Props {
  opened: boolean;
  onClose: () => void;
  rows: IncompleteRow[];
  entity: 'request' | 'intake' | 'case' | 'registration';
}

/** Rows drawn at once. The export always contains every row that matches, not just these. */
const SHOW_LIMIT = 200;

const ENTITY_LABEL = {
  request: 'Request', intake: 'Intake', case: 'Case', registration: 'Registration',
} as const;

/** Spreadsheet columns: what we know about the person, then what is missing. */
function csvColumns(entity: Props['entity']): TableColumn<IncompleteRow>[] {
  const col = (key: string, header: string, accessor: (r: IncompleteRow) => string | number) =>
    ({ key, header, accessor, sortable: false }) as TableColumn<IncompleteRow>;
  return [
    col('reference', ENTITY_LABEL[entity] === 'Case' ? 'Case name' : `${ENTITY_LABEL[entity]} reference`, (r) => r.reference),
    col('id', `${ENTITY_LABEL[entity]} ID`, (r) => r.id),
    col('who', 'Person', (r) => r.who),
    col('firstName', 'First name', (r) => r.firstName),
    col('surname', 'Surname', (r) => r.surname),
    col('email', 'Email', (r) => r.email),
    col('phone', 'Phone', (r) => r.phone),
    col('gender', 'Gender', (r) => r.gender),
    col('dateOfBirth', 'Date of birth', (r) => r.dateOfBirth),
    col('dobReceived', 'Date of birth as received', (r) => r.dobReceived),
    col('location', 'Location', (r) => r.location),
    col('missingText', 'Missing', (r) => r.missingText),
  ];
}

/** After the run: who is still missing details, viewable here and exportable as a spreadsheet. */
export function IncompletePeopleModal({ opened, onClose, rows, entity }: Props) {
  const [search, setSearch] = useState('');
  const [field, setField] = useState<IncompleteKey | null>(null);

  // A fresh look every time it opens.
  useEffect(() => { if (opened) { setSearch(''); setField(null); } }, [opened]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (field && !r.keys.includes(field)) return false;
      if (!term) return true;
      return [r.label, r.reference, r.email, r.phone, String(r.id)]
        .some((v) => v.toLowerCase().includes(term));
    });
  }, [rows, search, field]);

  const people = rows.filter((r) => r.who !== 'Whole record').length;
  const recordGaps = rows.length - people;
  const noun = ENTITY_LABEL[entity].toLowerCase();

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title="People with missing details">
      <Stack gap="md">
        <Text size="sm">
          <b>{people.toLocaleString()}</b> {people === 1 ? 'person is' : 'people are'} still missing
          details after every lookup
          {recordGaps > 0 && (
            <>, plus <b>{recordGaps.toLocaleString()}</b> {noun}{recordGaps === 1 ? '' : 's'} with a
            gap of their own (a failed lookup or no location)</>
          )}
          . Export the list to chase them up.
        </Text>

        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            style={{ flex: 1, minWidth: 200 }}
            placeholder="Search name, email, phone or reference"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Select
            w={220}
            placeholder="Any missing field"
            data={INCOMPLETE_FILTER_OPTIONS}
            value={field}
            onChange={(v) => setField(v as IncompleteKey | null)}
            clearable
          />
        </Group>

        {shown.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">Nothing matches.</Text>
        ) : (
          <ScrollArea.Autosize mah={420} type="auto">
            <Table stickyHeader highlightOnHover verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{ENTITY_LABEL[entity]}</Table.Th>
                  <Table.Th>Person</Table.Th>
                  <Table.Th>Who</Table.Th>
                  <Table.Th>Missing</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.slice(0, SHOW_LIMIT).map((r, i) => (
                  <Table.Tr key={`${r.id}-${r.who}-${i}`}>
                    <Table.Td>
                      {recordPath(entity, r.id) ? (
                        <Anchor
                          href={recordPath(entity, r.id)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          fw={600}
                          title="Open in a new tab"
                        >
                          {r.reference} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
                        </Anchor>
                      ) : (
                        <Text size="sm" fw={600}>{r.reference}</Text>
                      )}
                      <Text size="xs" c="dimmed">#{r.id}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{r.who}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" c={r.label === 'Unknown person' ? 'dimmed' : undefined}>
                        {r.who === 'Whole record' ? '—' : r.label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {r.missing.map((m) => (
                          <Badge
                            key={m}
                            size="sm"
                            variant="light"
                            color={r.keys.includes('lookupFailed') ? 'red' : 'orange'}
                          >
                            {m}
                          </Badge>
                        ))}
                      </Group>
                      {r.dobReceived && (
                        <Text size="xs" mt={4}>Date of birth received: <code>{r.dobReceived}</code></Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}

        {shown.length > SHOW_LIMIT && (
          <Text size="xs" c="dimmed">
            Showing the first {SHOW_LIMIT} of {shown.length.toLocaleString()}. The export has all of them.
          </Text>
        )}

        <Group justify="space-between" mt="xs">
          <Button variant="default" onClick={onClose}>Close</Button>
          <Button
            leftSection={<IconDownload size={16} />}
            disabled={shown.length === 0}
            onClick={() => downloadCsv(shown, csvColumns(entity), `${entity}-incomplete-people`)}
          >
            Export {shown.length.toLocaleString()} to spreadsheet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
